'use client'

import {
    Order,
    OrderStatus,
    PROCEDURE_STATUS_LABELS,
    PROCEDURE_STATUS_COLORS,
    getOrdersByClient,
} from '@/lib/api/orders/order-api'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdArrowForward,
    MdMedicalServices,
    MdPerson,
    MdAccessTime,
    MdMeetingRoom,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import { setProntuarioOrigin } from '@/lib/prontuario/prontuario-origin'
import { RecordActionsMenu } from '@/components/medical/record-actions-menu'
import { useProntuarioData, useProntuarioNavigate } from './prontuario-context'

export default function ProcedimentosPage() {
    const { client, clientId } = useProntuarioData()
    const navigate = useProntuarioNavigate()

    const [orders, setOrders] = useState<Order[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (clientId) {
            loadOrders()
        }
    }, [clientId])

    const loadOrders = async () => {
        if (!clientId) return

        setLoading(true)
        try {
            const response = await getOrdersByClient(parseInt(clientId, 10))
            if (response.success) {
                setOrders(response.result || [])
            } else {
                toast.error(response.error || 'Erro ao carregar procedimentos')
            }
        } catch (error) {
            toast.error('Erro ao carregar procedimentos')
        } finally {
            setLoading(false)
        }
    }

    const formatCentsLabel = (cents: number): string => {
        return `R$ ${(cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando procedimentos...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-base-content">Procedimentos</h2>
                    <p className="text-sm text-base-content/60">
                        Histórico de procedimentos do paciente
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm gap-1"
                        onClick={() => {
                            // Guarda a volta ANTES de sair: a partir daqui o
                            // operador atravessa agendamento, cobrança e detalhe —
                            // telas que não sabem de onde ele veio.
                            setProntuarioOrigin(
                                `/person/clients/${clientId}/prontuario/procedimentos`,
                                client?.name || 'paciente'
                            )
                            navigate(
                                `/atendimento/scheduling?type=PROCEDURE&clientId=${clientId}`
                            )
                        }}
                    >
                        <MdAdd className="text-base" />
                        Novo procedimento
                    </button>
                <span className="badge badge-lg badge-ghost">
                    {orders.length} {orders.length === 1 ? 'procedimento' : 'procedimentos'}
                </span>
                </div>
            </div>

            {/* Orders List */}
            {orders.length === 0 ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-8 text-center">
                    <MdMedicalServices className="text-4xl mx-auto mb-3 text-base-content/20" />
                    <p className="text-base-content/60">Nenhum procedimento registrado</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {orders.map(order => (
                        <div
                            key={order.id}
                            className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                            onClick={() => navigate(`/bookings/orders/${order.id}`)}
                        >
                            <div className="p-5">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="p-2 bg-secondary/10 rounded-lg">
                                                <MdMedicalServices className="text-xl text-secondary" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-bold text-base-content truncate">
                                                    {order.catalog?.name || 'Procedimento'}
                                                </h3>
                                                <p className="text-sm text-base-content/60">
                                                    {order.scheduledAt
                                                        ? format(new Date(order.scheduledAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
                                                        : format(new Date(order.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
                                                    }
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap gap-2 mb-3">
                                            <span className={`badge ${PROCEDURE_STATUS_COLORS[order.status]} text-white`}>
                                                {PROCEDURE_STATUS_LABELS[order.status]}
                                            </span>
                                            {order.catalog?.catalogType && (
                                                <span className="badge badge-ghost">
                                                    {order.catalog.catalogType.name}
                                                </span>
                                            )}
                                            {order.team && order.team.length > 0 && (
                                                <span className="badge badge-ghost gap-1">
                                                    <MdPerson className="text-sm" />
                                                    {order.team.length} profissional(is)
                                                </span>
                                            )}
                                        </div>

                                        {order.description && (
                                            <p className="text-sm text-base-content/70 line-clamp-2 mb-3">
                                                {order.description}
                                            </p>
                                        )}

                                        {order.outcome && (
                                            <p className="text-sm text-base-content/70 line-clamp-1 mb-3">
                                                <span className="font-medium">Desfecho:</span> {order.outcome}
                                            </p>
                                        )}

                                        <div className="flex flex-wrap items-center gap-4 text-xs text-base-content/50">
                                            {order.professional && (
                                                <div className="flex items-center gap-1">
                                                    <MdPerson className="text-sm" />
                                                    <span>{order.professional.name}</span>
                                                </div>
                                            )}
                                            {order.room && (
                                                <div className="flex items-center gap-1">
                                                    <MdMeetingRoom className="text-sm" />
                                                    <span>{order.room}</span>
                                                </div>
                                            )}
                                            {order.duration && (
                                                <div className="flex items-center gap-1">
                                                    <MdAccessTime className="text-sm" />
                                                    <span>{order.duration} min</span>
                                                </div>
                                            )}
                                            {order.catalog?.price != null && order.catalog.price > 0 && (
                                                <span className="font-semibold text-secondary">
                                                    {formatCentsLabel(order.catalog.price)}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="ml-2 flex items-center gap-1">
                                        <RecordActionsMenu
                                            kind="order"
                                            id={order.id}
                                            clientEmail={client?.email}
                                        />
                                        <span
                                            className="btn btn-ghost btn-sm btn-square"
                                            title="Ver detalhes"
                                        >
                                            <MdArrowForward className="text-lg" />
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
